import { useRef, useState, useEffect } from 'react';
import { createSyntheticEvent } from '../../../utils/utils';
import ErrorText from '../../Text/ErrorText/ErrorText';
import { useDatadogLog } from '../logging';

import styles from './Checkbox.module.scss';

type OnChangeProps<T> = {
  id?: T;
  isEnabled?: boolean;
};

export type CheckboxProps<T> = {
  name: T;
  variant?: 'default' | 'rounded' | 'border' | 'grey' | 'paylink' | 'outlineShadow';
  label?: React.ReactNode;
  labelPosition?: 'left' | 'right';
  labelSize?: string;
  value: boolean;
  error?: string | null;
  disabled?: boolean;
  readOnly?: boolean;
  size?: 'xsm' | 'sm' | 'lg';
  color?: 'primary' | 'secondary';
  hasNoPadding?: boolean;
  index?: number;
  style?: {
    '--width'?: string;
    '--height'?: string;
    '--boxColorChecked'?: string;
    '--checkmarkColor'?: string;
    '--borderThickness'?: string;
    '--boxBorderColorUnchecked'?: string;
    '--boxColorReadOnly'?: string;
  };
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, props: OnChangeProps<T>) => void;
};

const Checkbox = <T extends string>(props: CheckboxProps<T>): React.JSX.Element => {
  const {
    name,
    variant = 'default',
    label,
    labelPosition = 'left',
    labelSize,
    value,
    error = null,
    size = 'sm',
    color = 'primary',
    hasNoPadding,
    disabled = false,
    readOnly = false,
    style = {
      '--width': '20px',
      '--height': '20px',
      '--boxColorChecked': 'black',
      '--checkmarkColor': 'white',
      '--borderThickness': '0.09em',
      '--boxBorderColorUnchecked': '#c4c4c4',
      '--boxColorReadOnly': '#efefef',
    },
    onBlur = () => {},
    onChange = () => {},
  } = props;
  const [isEnabled, setIsEnabled] = useState(value);
  const wrapperEl = useRef<HTMLInputElement>(null);
  const log = useDatadogLog();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    wrapperEl.current?.blur();

    setIsEnabled(!isEnabled);
    onChange(event, { id: name, isEnabled: !isEnabled });
  };

  const handlerBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur(event);
  };

  useEffect(() => {
    setIsEnabled(value);
  }, [value]);

  const handleClick = (): void => {
    setIsEnabled(!isEnabled);
    const syntheticEvent = createSyntheticEvent(name, !isEnabled);
    const checkboxEvent = {
      ...syntheticEvent,
      target: { ...syntheticEvent.target, checked: !isEnabled },
    } as React.ChangeEvent<HTMLInputElement>;

    log({
      message: `${name} was toggled`,
      payload: { name, value },
      actionType: 'Option',
    });

    // TODO Change to a real event
    handleChange(checkboxEvent);
  };

  return (
    <div
      className={[styles[`container-${variant}`], styles.container, styles[getNoPaddingClass()]].join(' ')}
      onClick={(e) => {
        e.preventDefault();
        if (!disabled && !readOnly) {
          handleClick();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          if (!disabled && !readOnly) {
            handleClick();
          }
        }
      }}
      role="checkbox"
      aria-checked={isEnabled}
      tabIndex={0}
    >
      {label && labelPosition === 'left' && (
        <p
          style={{ fontSize: labelSize }}
          className={[styles.label, styles[getLabelClass()], styles[getDisabledClass()]].join(' ')}
        >
          {label}
        </p>
      )}
      <div
        id={name}
        className={[styles.container, styles[size], styles[color]].join(' ')}
        style={style}
        role="button"
        tabIndex={0}
        onBlur={handlerBlur}
        ref={wrapperEl}
        aria-label="check checkbox"
        aria-labelledby={`Checkbox-${name}`}
      >
        <input
          type="checkbox"
          id={`Checkbox-${name}`}
          className={[styles[variant], styles[getDisabledClass()], styles[getReadOnlyClass()]].join(' ')}
          name={name}
          onChange={handleChange}
          checked={isEnabled}
          disabled={disabled || readOnly}
        />
        <label
          className={[
            styles[variant],
            styles[getActiveClass()],
            styles[getReadOnlyClass()],
            styles[getDisabledClass()],
            styles[getErrorClass()],
          ].join(' ')}
          htmlFor={`Checkbox-${name}`}
          data-testid={`checkbox-${name}`}
        >
          {/*  */}
          {label && labelPosition === 'right' && (
            <p
              style={{ fontSize: labelSize, paddingLeft: '12px' }}
              className={[styles.label, styles[getLabelClass()], styles[getDisabledClass()]].join(' ')}
            >
              {label}
            </p>
          )}
        </label>
        <div className={[styles.errorContainer, styles[getErrorClass()]].join(' ')}>
          <ErrorText error={error} size="sm" indentation />
        </div>
      </div>
    </div>
  );

  function getActiveClass(): string {
    return disabled ? '' : 'active';
  }

  function getLabelClass(): string {
    return isEnabled ? 'isEnabled' : '';
  }

  function getErrorClass() {
    return error !== '' && error !== null ? 'hasError' : '';
  }

  function getReadOnlyClass() {
    return readOnly ? 'readOnly' : '';
  }

  function getDisabledClass() {
    return disabled ? 'disabled' : '';
  }
  function getNoPaddingClass() {
    return hasNoPadding ? 'noPadding' : '';
  }
};

export default Checkbox;
